<script lang="ts" setup>
import { computed, ref, watch, useTemplateRef, onBeforeUnmount, nextTick } from "vue";
import MarkerDisplay from "./MarkerDisplay.vue";
import Distance from "../player/Distance.vue";
import Dropdown from "../player/Dropdown.vue";
import { EventBus } from "@/utils/EventBus";
import { ComputedMarker, MarkerCategory } from '@/shared/types/zod';
import { useStore } from "@/store/Store";
import { useLiveStore } from "@/store/LiveStore";
import { storeToRefs } from "pinia";
import { TPoint } from '@/shared/types/geojson';
import { imageUrl } from "@/utils/ImageUrl";
import ArrowIcon from "./icons/ArrowIcon.vue";
import { useSwipeToClose } from "@/utils/swipeToClose";

const props = defineProps<{
	open?: boolean;
	slideMode?: boolean;
	iframed?: boolean;
	editor?: boolean;
}>();

const emit = defineEmits<{
	toggle: [value: boolean];
	slideOffset: [offset: number | null];
}>();

const store = useStore();
const liveStore = useLiveStore();

const layerViewEl = useTemplateRef("layerViewEl");
const expandedCategoryIds = ref<string[]>([]);

const { map, markerCategories, selectedRouteId, dropRoutes, categoryFilter, computedMarkersAll, uiText } = storeToRefs(store);

const logoUrl = computed(() => {
	if (!map.value?.logo?.imageUrl) return undefined
	return imageUrl(map.value.logo.imageUrl, 360 * 2)
});

function getMarkers(cat: MarkerCategory): ComputedMarker[] {
	return computedMarkersAll.value.filter(marker => marker.properties.__categoryId === cat.id);
};

const filteredMarkerCategories = computed(() => {
	return markerCategories.value.flatMap((category) => {
		const markers = getMarkers(category);
		return markers.length > 0 ? [{ category, markers }] : [];
	});
});

const hasCategoryFilter = computed(() => {
	return categoryFilter.value.length > 0;
});

function localized(item: any, key: string): string {
	return store.localized(item, key);
};

function getCategoryLabel(cat: MarkerCategory): string {
	const markers = getMarkers(cat);
	if (markers.length > 1)
		return localized(cat, "label");
	else
		return localized(markers[0], "label") || localized(cat, "label");
};

function markerClicked(marker: TPoint | any) {
	const categoryId = marker.properties?.__categoryId;
	if (hasCategoryFilter.value && categoryId && !categoryFilter.value.includes(categoryId)) {
		store.toggleCategoryFilter(categoryId);
	}
	// nextTick is needed for the toggle case above where the marker is not visible in this tick.
	nextTick(() => {
		EventBus.emit("showPopup", { id: marker.id, fly: true });
	});
	if (props.slideMode)
		setOpen(false);
};

function categoryClicked(cat: MarkerCategory, markers: ComputedMarker[]) {
	if (markers.length > 1) {
		toggleCategoryExpansion(cat);
		return;
	}
	const marker = markers[0];
	if (marker) markerClicked(marker);
}

function filterCategory(cat: MarkerCategory) {
	const newValue = store.toggleCategoryFilter(cat.id);
	if (store.computedMarkersNotGenerated.length > 0 && newValue) {
		EventBus.emit("zoomTo", { features: store.computedMarkersNotGenerated, speed: "fast" });
	}
};

function clearCategoryFilter() {
	store.clearCategoryFilter();
}

function showFilteredMap() {
	setOpen(false);
}

function isFiltered(cat: MarkerCategory): boolean {
	return categoryFilter.value.includes(cat.id);
};

function isExpanded(cat: MarkerCategory): boolean {
	return expandedCategoryIds.value.includes(cat.id);
}

function visibleMarkers(cat: MarkerCategory, markers: ComputedMarker[]): ComputedMarker[] {
	return isExpanded(cat) ? markers : [];
}

function toggleCategoryExpansion(cat: MarkerCategory) {
	if (isExpanded(cat)) {
		expandedCategoryIds.value = expandedCategoryIds.value.filter(id => id !== cat.id);
	} else {
		expandedCategoryIds.value = [...expandedCategoryIds.value, cat.id];
	}
}

function routeClicked(routeId: string | undefined) {
	if (!routeId) return;
	store.setSelectedRouteId(routeId);
	EventBus.emit("routeClicked");
	setOpen(false);
};

//click outside
function openClickHandler(event: MouseEvent) {
	if (props.slideMode) {
		if (!layerViewEl.value || !layerViewEl.value.contains(event.target as Node)) {
			setOpen(false);
		}
	}
};

function setOpen(value: boolean) {
	emit("toggle", value);
};

const swipe = useSwipeToClose({
	el: layerViewEl,
	enabled: () => !!props.slideMode && !!props.open,
	onOffset: (offset) => emit("slideOffset", offset),
	onClose: () => setOpen(false),
});

watch(() => props.open, (newOpen) => {
	if (newOpen) {
		EventBus.emit("closePopup");
		window.addEventListener("click", openClickHandler, true);
	} else {
		window.removeEventListener("click", openClickHandler, true);
		swipe.reset(false);
	}
});

onBeforeUnmount(() => {
	window.removeEventListener("click", openClickHandler, true);
	swipe.reset();
});

</script>



<template>
	<div ref="layerViewEl" class="layer-view styled-scrollbars"
		:class="{ iframed, 'slide-mode': slideMode, 'filter-active': hasCategoryFilter }" v-if="map"
		@pointerdown="swipe.onPointerDown" @pointermove="swipe.onPointerMove" @pointerup="swipe.onPointerUp"
		@pointercancel="swipe.onPointerCancel" @click.capture="swipe.onClickCapture">

		<img v-if="logoUrl" :src="logoUrl" class="logo" :class="{ fill: map.logo.fill }">
		<div class="identity">
			<p v-if="dropRoutes.length" class="kicker">{{ map.name }}</p>
			<Dropdown v-if="dropRoutes.length" class="route-heading" :options="dropRoutes" :value="selectedRouteId"
				@change="routeClicked" />
			<h1 v-else>{{ map.name }}</h1>
		</div>

		<div class="header-meta">
			<Distance :editor="editor" />
		</div>
		<hr>

		<ul>
			<template v-for="{ category: cat, markers } in filteredMarkerCategories" :key="cat.id">
				<li class="group" :class="{
					dimmed: categoryFilter.length > 0 && !isFiltered(cat),
					expandable: markers.length > 1,
					expanded: isExpanded(cat)
				}" @click="categoryClicked(cat, markers)">
					<span class="category-main">
						<span class="category-main-hit">
							<marker-display class="marker" :icon="cat.icon" :color="cat.color" :square="true" />
							<span class="ellipsis category-label">{{ getCategoryLabel(cat) }}</span>
							<ArrowIcon v-if="markers.length > 1" class="category-chevron"
								:direction="isExpanded(cat) ? 'up' : 'down'" />
						</span>
					</span>
					<label class="category-trailing" @click.stop>
						<span v-if="markers.length > 1" class="category-count">{{ markers.length }}</span>
						<input class="category-filter-checkbox" type="checkbox" :checked="isFiltered(cat)"
							:aria-label="`${uiText.filterLabel}: ${getCategoryLabel(cat)}`" @click.stop="filterCategory(cat)" />
					</label>
				</li>
				<li v-for="(marker, markerIndex) in visibleMarkers(cat, markers)" :key="marker.id" class="marker-item" :class="{
					dimmed: categoryFilter.length > 0 && !isFiltered(cat),
					'last-marker-item': markerIndex === markers.length - 1
				}" @click.stop="markerClicked(marker)">
					<marker-display class="marker-item-icon" :icon="cat.icon" :color="cat.color" :square="true" />
					<span class="ellipsis">{{ localized(marker, 'label') || getCategoryLabel(cat) }}</span>
				</li>
			</template>

			<template v-for="runner in liveStore.resultFeatures" :key="runner.id">
				<li class="group" @click="markerClicked(runner)">
					<!-- <marker-view type="live" :color="''" :passed="true" :text="runner.properties.initials" :arrow="false" :isSidebar="true" /> -->
					<marker-display class="marker" icon="live" :square="true" :color="runner.properties.color"
						:mergeProps="runner.properties" />
					{{ runner.properties.label }}
				</li>
			</template>
		</ul>

		<div v-if="slideMode && hasCategoryFilter" class="filter-actions">
			<button class="filter-action secondary" type="button" @click="clearCategoryFilter">
				{{ uiText.clear }} ({{ categoryFilter.length }})
			</button>
			<button class="filter-action primary" type="button" @click="showFilteredMap">
				{{ uiText.show }}
			</button>
		</div>
	</div>
</template>



<style scoped lang="scss">
.layer-view {
	font-size: 15px;
	color: #444;
	/* position: absolute;
		top: 50px;
		top: 6px;
		left: 6px; */
	/* overflow: visible; */
	position: relative;
	display: flex;
	flex-flow: column nowrap;
	align-items: flex-start;
	background-color: white;
	height: 100%;
	padding: 1rem;
	padding-right: .75rem;
	width: min(var(--sidebar-width), calc(100vw - var(--map-peek)));
	max-width: calc(100vw - var(--map-peek));
	flex: 0 0 auto;
	overflow-y: auto;
	overflow-x: hidden;
	padding-bottom: 1rem;

	z-index: 1000000010;
	border-right: solid 1px #bbb;
}

.layer-view.iframed {
	font-size: 12px;
}

.layer-view.slide-mode {
	/* Keep vertical scrolling; claim horizontal pans for swipe-to-close. */
	touch-action: pan-y;
}

.iframed .distance {
	// transform-origin: center left;
	font-size: .7rem;
}

.identity {
	width: 100%;
}

.kicker {
	margin: 0.85rem 0 0;
	font-size: 0.85rem;
	font-weight: 500;
	color: #777;
}

.route-heading {
	width: 100%;
	margin-top: 0.15rem;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 4px;
}

.route-heading :deep(p) {
	flex: 1 1 auto;
	min-width: 0;
	margin: 0;
	padding-right: 0 !important;
	margin-right: 0 !important;
	border-right: 0 !important;
	font-size: 1.35em;
	font-weight: bold;
	line-height: 1.45;
	color: #444;
}

.route-heading :deep(.icon) {
	width: 18px;
	height: 18px;
	margin-top: 2px;
	opacity: 0.45;
}

.route-heading :deep(.box) {
	top: 100%;
	left: 0;
	margin-top: 4px;
	width: 100%;
}

h1 {
	font-size: 1.35em;
	font-weight: bold;
	line-height: 1.45;
	margin-top: 0.85rem;
	margin-bottom: 0;
}

.header-meta {
	width: 100%;
}

.marker {
	margin-right: 9px;
	flex: 0 0 auto;
	border-radius: 4px !important;
	width: 28px !important;
	height: 28px !important;
	transition: opacity .15s ease;
}

.iframed .marker {
	width: 24px !important;
	height: 24px !important;
}

.hidden {
	opacity: .5;
}

ul {
	list-style-type: none;
	padding: 0;
	margin: 0;
	font-weight: normal;
	margin-top: 1rem;
	width: calc(100%)
}

.filter-mode-button {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 30px;
	margin-top: 12px;
	padding: 0 10px;
	border: 0;
	border-radius: 4px;
	background: #f1f1f1;
	color: #333;
	font: inherit;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
}

.filter-mode-button img {
	width: 16px;
	height: 16px;
	filter: brightness(0);
	opacity: .65;
}

.filter-mode-button.active {
	background: #e1e1e1;
}

li {
	/* font-weight: bold; */
	margin-bottom: 9px;
	display: flex;
	align-items: center;
	padding-left: 0;
	cursor: pointer;
}

li.group {
	/* font-weight: bold; */
	margin-top: 4px;
	// margin-right: 5px;
	// min-height: 32px;
	// padding-right: 2px;
	transition: color .15s ease;
}

li.group:not(:has(.category-trailing)):hover {
	color: #000;
}

.category-main {
	display: flex;
	align-items: center;
	flex: 1 1 0;
	min-width: 0;
	align-self: stretch;
	margin-right: 0;
}

.category-main-hit {
	display: flex;
	align-items: center;
	flex: 1 1 0;
	align-self: stretch;
	min-width: 0;
	padding: 0;
	transition: color .15s ease;
}

.category-main-hit:hover .category-label {
	color: #000;
}

.category-chevron {
	flex: 0 0 auto;
	width: 0;
	height: 14px;
	margin: 0;
	opacity: 0;
	overflow: hidden;
	pointer-events: none;
	color: currentColor;
	transition: opacity .15s ease, width .15s ease, margin .15s ease;
}

li.group.expandable .category-main-hit:hover .category-chevron,
li.group.expandable.expanded .category-chevron {
	width: 14px;
	height: 14px;
	margin: -2px -2px -2px 5px;
	overflow: visible;
	opacity: .45;
}

.category-label {
	flex: 0 1 auto;
	min-width: 0;
}

.category-trailing {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	align-self: stretch;
	flex: 0 0 auto;
	padding: 0;
	cursor: pointer;
}

.category-trailing:hover .category-count {
	color: #000;
}

.category-trailing:hover .category-filter-checkbox:not(:checked) {
	border-color: #666;
}

li.marker-item {
	min-height: 29px;
	margin: 0 5px 2px 36px;
	padding: 3px 8px 3px 0;
	font-size: 13px;
	color: #626262;
	line-height: 1.35;
	transition: color .15s ease;
}

li.marker-item.last-marker-item {
	margin-bottom: 7px;
}

.iframed li.marker-item {
	min-height: 25px;
	margin-left: 32px;
	margin-bottom: 1px;
	padding-top: 2px;
	padding-bottom: 2px;
	font-size: 11px;
}

.iframed li.marker-item.last-marker-item {
	margin-bottom: 5px;
}

.marker-item-icon {
	width: 20px;
	height: 20px;
	border-radius: 4px;
	margin-right: 8px;
	flex: 0 0 auto;
	transition: opacity .15s ease;
}

.iframed .marker-item-icon {
	width: 18px;
	height: 18px;
	margin-right: 7px;
}

li.marker-item:hover {
	color: #000;
}

li.group .spacer {
	flex: 1 1 auto;
}

li.group .check {
	margin-left: 6px;
}

.logo {
	/* max-width: 250px; */
	max-width: 100%;
	align-self: center;
}

.logo.fill {
	max-width: calc(100% + 1.75rem);
	margin: -1rem -.75rem 0 -1rem;
}

hr {
	margin: 1rem 0 0;
	border: 0;
	border-top: solid 1px #999;
	width: 100%;
}

.icon {
	margin-left: 4px;
}

.opener {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	cursor: pointer;
}

p.opener {
	margin: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.race-desc {
	font-size: 14px;
	margin-top: .5rem;
	margin-bottom: .5rem;
	font-weight: 100;
}

li.group.dimmed,
li.marker-item.dimmed {
	color: #777;
}

li.group.dimmed .marker,
li.marker-item.dimmed .marker-item-icon {
	opacity: 0.3;
}

.category-count {
	min-width: 1.25em;
	font-size: 14px;
	line-height: 1;
	color: #777;
	font-variant-numeric: tabular-nums;
	text-align: right;
	transition: color .15s ease;
}

.iframed .category-count {
	font-size: inherit;
}

.category-filter-checkbox {
	appearance: none;
	-webkit-appearance: none;
	display: block;
	box-sizing: border-box;
	flex: 0 0 20px;
	width: 20px;
	min-width: 0;
	height: 20px;
	margin: 0 0 0 12px;
	border: 1.5px solid #c4c4c4;
	border-radius: 3px;
	background: #fff;
	cursor: pointer;
	position: relative;
	overflow: hidden;
	transition: width 0.15s ease, flex-basis 0.15s ease, margin-left 0.15s ease, opacity 0.12s ease, border-width 0.15s ease, border-color 0.15s ease;
}

.category-filter-checkbox:checked {
	border-color: #222;
	background-color: #222;
}

.category-filter-checkbox:checked::after {
	content: "";
	position: absolute;
	left: 6px;
	top: 2px;
	width: 5px;
	height: 10px;
	border: solid #fff;
	border-width: 0 1.5px 1.5px 0;
	transform: rotate(45deg);
}

.category-filter-checkbox:focus-visible {
	outline: 2px solid #222;
	outline-offset: 2px;
}

@media (hover: hover) {
	.layer-view:not(.slide-mode):not(.filter-active) .category-filter-checkbox {
		// width: 0;
		// flex-basis: 0;
		// margin-left: 0;
		// border-width: 0;
		// opacity: 0;
		// pointer-events: none;
		opacity: 0.4;
	}

	.layer-view:not(.slide-mode):not(.filter-active):hover .category-filter-checkbox {
		// width: 20px;
		// flex-basis: 20px;
		// margin-left: 12px;
		// border-width: 1.5px;
		// pointer-events: auto;
		opacity: 1;
	}
}

.filter-actions {
	position: sticky;
	left: 0;
	right: 0;
	bottom: -1rem;
	display: flex;
	gap: 10px;
	width: 100%;
	margin-top: auto;
	padding-top: 12px;
	padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);
	margin-bottom: -1rem;
	background: #fff;
	border-top: solid 1px #eee;
	z-index: 3;
}

.filter-action {
	appearance: none;
	border: 1px solid transparent;
	border-radius: 999px;
	height: 38px;
	padding: 0 18px;
	font: inherit;
	font-weight: 600;
	cursor: pointer;
}

.filter-action.secondary {
	background: white;
	border-color: #c7c7c7;
	color: #222;
}

.filter-action.primary {
	flex: 1 1 auto;
	background: #222;
	color: white;
}

.filter-action.primary:hover {
	background: #444;
}

.filter-action.secondary:hover {
	background: #f3f3f3;
}

.filter-action:focus-visible {
	outline: 2px solid #222;
	outline-offset: 2px;
}

@media (min-width: 951px) {
	.layer-view {
		padding-bottom: 1rem;
	}
}
</style>